
import { useField } from "formik";
import React, { useState } from "react";
import { AutoComplete } from "primereact/autocomplete";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../helpers/fetchData";


const sharedPt = (lang) => ({
  input: {
    root: {
      className: "input-field !opacity-100",
    },
  },
  panel: { className: "!shadow-lg rounded-[8px] mt-2 pb-2 overflow-hidden" },
  item: { className: "hover:text-primary capitalize" },
  loadingIcon: { className: `absolute top-[13px] ${lang === 'en' ? 'right-[12px]' : 'left-[12px]'}` },
  emptymesage: { className: "!text-center" },
});

const defaultOptionsFormat = (response) => {
  return response?.map((data) => {
    return {
      label: data?.name,
      value: data?.id,
    };
  });
};

const fetchOptions = async (
  query,
  fieldUrl,
  setSuggestions,
  optionsFormat,
  setIsLoading
) => {
  const searchValue = query?.query || query?.search;

  if (!searchValue || searchValue.trim().length < 3) return;
  setIsLoading(true);
  try {
    let payload = query;
    const response = await fetchData("POST", `${fieldUrl}`, payload);
    if (response?.data) {
      let options = optionsFormat(response?.data);
      setSuggestions(options);
    } else {
      setSuggestions([]);
    }
  } catch (e) {
    console.log(e);
  } finally {
    setIsLoading(false);
  }
};

const FormikAutoCompleteField = ({
  label,
  name,
  fieldUrl,
  className,
  parentClass = "",
  emptyMessage = "No searched results found",
  optionsFormat = defaultOptionsFormat,
  queryKey = "query",
  payload = {},
  NavigationIcon,
  onIconClick = () => { },
  ...props
}) => {
  const [field, meta] = useField(name);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setIsLoading] = useState(false);
  const { i18n } = useTranslation();
  const handleComplete = (event) => {
    fetchOptions(
      { [queryKey]: event.query, ...payload },
      fieldUrl,
      setSuggestions,
      optionsFormat,
      setIsLoading
    );
  };

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${parentClass}`}>
      <div className="flex items-center justify-between">
        <label htmlFor={name} className=" input-label">
          {label}
        </label>
        {NavigationIcon && (
          <NavigationIcon
            size={17}
            className="text-primary cursor-pointer"
            onClick={onIconClick}
          />
        )}
      </div>
      <AutoComplete
        loadingIcon={
          loading ? (
            <i className={`pi pi-spin pi-spinner text-primary absolute top-[9px] ${i18n.language === 'en' ? 'right-[12px]' : 'left-[12px]'} `} />
          ) : (
            <span />
          )
        }
        id={name}
        field="label"
        {...field}
        {...props}
        suggestions={suggestions}
        completeMethod={handleComplete}
        forceSelection
        pt={sharedPt(i18n.language)}
        emptyMessage={emptyMessage}
        showEmptyMessage={true}
      />
      {meta.error && meta.touched && (
        <p className="text-sm text-red-500">{meta.error}</p>
      )}
    </div>
  );
};

const StandaloneAutoCompleteField = ({
  label,
  name,
  value = "",
  fieldUrl,
  className,
  parentClass = "",
  emptyMessage = "No searched results found",
  optionsFormat = defaultOptionsFormat,
  queryKey = "query",
  payload = {},
  ...props
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setIsLoading] = useState(false);
  const { i18n } = useTranslation();
  const handleComplete = (event) => {
    fetchOptions(
      { [queryKey]: event.query, ...payload },
      fieldUrl,
      setSuggestions,
      optionsFormat,
      setIsLoading
    );
  };

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${parentClass}`}>
      {label && (
        <label htmlFor={name} className="input-label">
          {label}
        </label>
      )}
      <AutoComplete
        loadingIcon={
          loading ? (
            <i className={`pi pi-spin pi-spinner text-primary absolute top-[9px] ${i18n.language === 'en' ? 'right-[12px]' : 'left-[12px]'}`} />
          ) : (
            <span />
          )
        }
        value={value}
        field="label"
        {...props}
        suggestions={suggestions}
        completeMethod={handleComplete}
        forceSelection
        pt={sharedPt(i18n.language)}
        emptyMessage={emptyMessage}
        showEmptyMessage={true}
      />
    </div>
  );
};

// wrapper
const AutoCompleteField = (props) => {
  const { useFormik = true } = props;

  if (useFormik) {
    return <FormikAutoCompleteField {...props} />;
  }

  return <StandaloneAutoCompleteField {...props} />;
};

export default AutoCompleteField;
